import { isSameThing, itemKey } from "./similar.js";
/**
 * Statuses that mean the thing is physically in the house.
 *
 * The complement of RESOLVED. Named separately rather than derived from it
 * because this is the question the cart asks — "have we got any?" — and reading
 * it as a double negative is how you end up offering to buy milk you own.
 */
export const IN_STOCK = new Set([
    "undated",
    "active",
    "awaiting",
]);
/**
 * Everything still in the house that is the same thing as `name`.
 *
 * Uses `isSameThing`, the same matcher that groups the larder list, so the
 * prompt can never contradict what the screen is showing: if the list shows
 * "Milk ×2" then eating one cannot claim you have run out.
 */
export function stillStocked(name, all, exceptId) {
    return all.filter((i) => i.id !== exceptId && IN_STOCK.has(i.status) && isSameThing(name, i.name));
}
/**
 * Should we ask whether they want to buy this again?
 *
 * Three gates, cheapest first:
 *
 *   1. Is there another one in the house? Then nothing ran out and there is
 *      nothing to buy. This is the gate that keeps the prompt from becoming a
 *      tax on the core loop — most consumption is not the last of anything.
 *   2. Did they already say "never again"? Then we do not re-ask, ever.
 *   3. Is it already on the cart? Then the answer is on screen already.
 */
export function shouldOfferRebuy({ name, itemId, items, cart, memory, }) {
    if (stillStocked(name, items, itemId).length > 0)
        return false;
    if (memory?.suppressRebuy)
        return false;
    if (isOnCart(name, cart))
        return false;
    return true;
}
/**
 * What this household last paid for this thing.
 *
 * The honest answer to "what does it cost", and the only one this app can give.
 * A live shop price is per-pincode and per-minute — a cached one would be a
 * confidently wrong number, which is the failure AGENTS.md rule 1 forbids for
 * dates and which is no better here. What you paid last month is a fact, it is
 * already in the log, and it is the number that tells you whether ₹90 on a
 * screen is a good deal. See ADR 0023.
 *
 * Matched with `isSameThing`, so the milk you dictated and the "Amul Milk 1L"
 * you scanned are one product's price history.
 */
export function lastPaid(name, all) {
    let best = null;
    for (const i of all) {
        if (i.priceMinor === null)
            continue;
        if (!isSameThing(name, i.name))
            continue;
        if (best === null || i.purchasedAt > best.at) {
            best = { minor: i.priceMinor, at: i.purchasedAt };
        }
    }
    return best;
}
/** Is this thing already on the cart? Matched loosely, like everything else. */
export function isOnCart(name, cart) {
    const key = itemKey(name);
    if (!key)
        return false;
    return cart.some((e) => e.key === key || isSameThing(name, e.name));
}
/** Folds one verdict into a household's memory of a product. */
export function applyVerdict(previous, { key, label, verdict, at, }) {
    const base = previous ?? {
        key,
        label,
        loved: 0,
        fine: 0,
        neverAgain: 0,
        lastVerdict: null,
        lastRatedAt: null,
        suppressRebuy: false,
    };
    return {
        ...base,
        // The longest name wins, same rule as `groupByName`: "Amul Milk 1L" is a
        // more useful label than the "milk" someone dictated in a hurry.
        label: label.length > base.label.length ? label : base.label,
        loved: base.loved + (verdict === "loved" ? 1 : 0),
        fine: base.fine + (verdict === "fine" ? 1 : 0),
        neverAgain: base.neverAgain + (verdict === "never_again" ? 1 : 0),
        lastVerdict: verdict,
        lastRatedAt: at,
        // Latest answer wins in both directions. Someone who writes a thing off and
        // later loves it has changed their mind, and the app should let them.
        suppressRebuy: verdict === "never_again",
    };
}
//# sourceMappingURL=cart.js.map